import { ApiFehler, hole, schicke } from './api'
import { holeAktiveRegistrierung } from './sw-registrierung'

export interface PushGeraet {
  id: string
  userAgent: string | null
  createdAt: string
  lastUsedAt: string | null
}

export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = globalThis.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

export function istIos(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  )
}

export function istInstalliert(): boolean {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    Boolean((navigator as unknown as { standalone?: boolean }).standalone)
  )
}

export function istPushUnterstuetzt(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

export function leseErlaubnis(): NotificationPermission | 'nicht-unterstuetzt' {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'nicht-unterstuetzt'
  }
  const notif = (window as unknown as { Notification?: { permission?: NotificationPermission } }).Notification
  if (!notif?.permission) {
    return 'nicht-unterstuetzt'
  }
  return notif.permission
}

export async function holeVapidSchluessel(): Promise<string | null> {
  try {
    const res = await hole<{ publicKey: string }>('/api/push/vapid')
    return res.publicKey
  } catch {
    return null
  }
}

export async function holeGeraete(): Promise<PushGeraet[]> {
  try {
    const res = await hole<{ geraete: PushGeraet[] }>('/api/push/geraete')
    return res.geraete
  } catch {
    return []
  }
}

export async function meldeGeraetAb(endpoint: string): Promise<void> {
  await schicke('DELETE', '/api/push/abo', { endpoint })
}

export async function aktivierePush(): Promise<{ erfolg: boolean; grund?: string }> {
  if (!istPushUnterstuetzt()) {
    return { erfolg: false, grund: 'nicht-unterstuetzt' }
  }

  // ⚠️ Der Erlaubnis-Dialog wird nie ungefragt ausgelöst; er kommt nach einem Tippen.
  const permission = await window.Notification.requestPermission()
  if (permission !== 'granted') {
    return { erfolg: false, grund: 'verweigert' }
  }

  const publicKey = await holeVapidSchluessel()
  if (!publicKey) {
    return { erfolg: false, grund: 'server-nicht-eingerichtet' }
  }

  const reg = await holeAktiveRegistrierung()
  if (!reg) {
    return { erfolg: false, grund: 'kein-service-worker' }
  }

  try {
    const appServerKey = urlBase64ToUint8Array(publicKey)
    let sub = await reg.pushManager.getSubscription()
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: appServerKey as unknown as BufferSource,
      })
    }

    const rawKey = sub.getKey('p256dh')
    const rawAuth = sub.getKey('auth')
    const p256dh = rawKey
      ? btoa(String.fromCharCode(...new Uint8Array(rawKey)))
      : ''
    const auth = rawAuth
      ? btoa(String.fromCharCode(...new Uint8Array(rawAuth)))
      : ''

    await schicke('POST', '/api/push/abo', {
      endpoint: sub.endpoint,
      p256dh,
      auth,
    })

    return { erfolg: true }
  } catch (err: unknown) {
    const nachricht = err instanceof ApiFehler ? err.message : 'Aktivierung fehlgeschlagen'
    return { erfolg: false, grund: nachricht }
  }
}
